import { fintracService } from "@services/apis/Fintrac";
import {
  FintracJobRun,
  FintracReport,
  FintracReportTransaction,
} from "@services/models/Fintrac/FintracModel";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { useCallback, useMemo, useState } from "react";
import TransactionPartyPanel from "./TransactionPartyPanel";
import {
  FintracHeader,
  FintracStatusPill,
  FintracTable,
  fintracAccountTypeColumn,
  fintracAmountColumn,
  fintracNumberColumn,
  fintracTextColumn,
  formatFintracAmount,
} from "./fintracChrome";
import { useFintracList } from "./useFintracList";

const TransactionsScreen = ({
  job,
  report,
  onBackToJobs,
  onBackToReports,
}: {
  job: FintracJobRun;
  report: FintracReport;
  onBackToJobs: () => void;
  onBackToReports: () => void;
}) => {
  const load = useCallback(
    async (page: number, limit: number) => {
      const response = await fintracService.listTransactions(report.id, {
        page,
        limit,
      });
      return {
        rows: response.data.transactions ?? [],
        pagination: response.data.pagination,
      };
    },
    [report.id],
  );

  const list = useFintracList(String(report.id), load);
  const [selected, setSelected] = useState<FintracReportTransaction | null>(
    null,
  );
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  const onRowClicked = (event: RowClickedEvent<FintracReportTransaction>) => {
    const target = event.event?.target;
    if (target instanceof Element && target.closest("[data-row-control]")) {
      return;
    }
    const row = event.data;
    if (!row) return;
    setPanelCollapsed(false);
    setSelected((current) => (current?.id === row.id ? null : row));
  };

  const columnDefs = useMemo<ColDef[]>(
    () => [
      fintracNumberColumn("reportId", "Report ID", 70, "Report ID copied!"),
      fintracTextColumn("transactionCreatedAt", "Transaction Time", 132),
      fintracNumberColumn(
        "transactionId",
        "Transaction ID",
        90,
        "Transaction ID copied!",
      ),
      fintracTextColumn("orderId", "Order Id", 200, "Order ID copied!"),
      fintracTextColumn(
        "merchantOrderId",
        "MOID",
        200,
        "Merchant order ID copied!",
      ),
      fintracNumberColumn("userId", "User Id", 70, "User ID copied!"),
      fintracNumberColumn(
        "merchantId",
        "Merchant Id",
        90,
        "Merchant ID copied!",
      ),
      fintracAccountTypeColumn("accountCategory", "Category", 120),
      fintracAccountTypeColumn("accountType", "Account Type", 120),
      fintracTextColumn(
        "merchantName",
        "Merchant (From KYB)",
        180,
        "Merchant (From KYB) copied!",
      ),
      fintracTextColumn("dba", "DBA", 100),
      fintracTextColumn(
        "authorisedPersonName",
        "Authorised Person (Name)",
        220,
      ),
      fintracTextColumn("emailId", "E-mail", 210, "E-mail copied!"),
      fintracTextColumn(
        "fromAddress",
        "From Address",
        200,
        "From address copied!",
      ),
      fintracTextColumn("toAddress", "To Address", 200, "To address copied!"),
      fintracTextColumn(
        "transactionHash",
        "Transaction Hash",
        200,
        "Transaction hash copied!",
      ),
      fintracTextColumn("cryptoSymbol", "Crypto", 58),
      fintracAmountColumn("amountUsd", "Amount", 80),
      fintracAmountColumn("amountCad", "CAD Amount", 90),
      fintracAmountColumn("usdCadFxRate", "Rate", 80),
    ],
    [],
  );

  return (
    <div className="flex h-full flex-col overflow-hidden bg-bg-page-body">
      <FintracHeader
        crumbs={[
          { label: "FINTRAC", onClick: onBackToJobs },
          { label: `Job ${job.id}`, onClick: onBackToReports },
          { label: `User ${report.userId}` },
        ]}
        detail={
          <span className="inline-flex items-center gap-2">
            <span>CAD total {formatFintracAmount(report.totalCadAmount)}</span>
            <FintracStatusPill status={report.status} />
          </span>
        }
      />
      <div className="flex min-h-0 flex-1 flex-row items-start">
        <div
          className={`flex min-h-0 flex-col overflow-hidden bg-bg-page-body px-[12px] py-[20px] transition-all duration-300 ${
            selected && !panelCollapsed ? "w-3/4" : "w-full"
          }`}
          style={{ height: "calc(100dvh - 70px)" }}
        >
          <div className="min-h-0 flex-1">
            <FintracTable
              columnDefs={columnDefs}
              rows={list.rows}
              loading={list.loading}
              loadingMore={list.loadingMore}
              error={list.error}
              totalRecords={list.pagination.totalRecords}
              hasMore={list.hasMore}
              onLoadMore={list.loadMore}
              onRowClicked={onRowClicked}
              selectedRowId={selected?.id}
            />
          </div>
        </div>
        {selected && (
          <div
            className={`relative border-l border-border-subtle bg-bg-page-body py-4 transition-all duration-300 ${
              panelCollapsed ? "w-0 px-0" : "w-1/4 px-2"
            }`}
            style={{ height: "calc(100dvh - 70px)" }}
          >
            <button
              type="button"
              onClick={() => setPanelCollapsed((current) => !current)}
              className="group absolute top-1/2 z-20 flex -translate-y-1/2 items-center justify-center rounded-lg bg-bg-button-secondary text-text-base shadow-lg transition-all duration-200"
              style={{
                left: panelCollapsed ? "calc(100% - 32px)" : "-16px",
                width: "32px",
                height: "60px",
              }}
              aria-label={panelCollapsed ? "Expand panel" : "Collapse panel"}
            >
              <span className="material-symbols-outlined text-[20px] transition-transform group-hover:scale-110">
                {panelCollapsed ? "chevron_left" : "chevron_right"}
              </span>
            </button>
            <div
              className={`h-full overflow-auto transition-opacity duration-300 ${
                panelCollapsed ? "pointer-events-none opacity-0" : "opacity-100"
              }`}
            >
              <TransactionPartyPanel transaction={selected} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionsScreen;
